import { createContext, useContext } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import {
  buildPinBatchedQueries,
  type PinBatchedQuery,
} from "src/analytics/dashboard/dashboardFilterParams";
import type {
  MetricsQueryRawResult,
  MetricsQueryResponse,
  MetricsQueryRow,
} from "src/analytics/metrics/useMetricsQuery";
import { useCurrentWorkspace } from "src/api/queries";
import type { Pin } from "src/clients/analytics-dashboard";
import { useEnvironment } from "src/hooks/useEnvironment";
import type { StrictDateRange } from "src/utils/timeWindow";

const METRICS_QUERY_PATH = "/analytics/api/v1/query";

type BatchedResults = Record<string, MetricsQueryRawResult>;

export const dashboardMetricsKeys = {
  all: ["dashboard-metrics"] as const,
  batch: (
    baseUrl: string | undefined,
    dataSource: string | undefined,
    queries: Record<string, PinBatchedQuery>,
  ) =>
    [
      ...dashboardMetricsKeys.all,
      baseUrl,
      dataSource,
      JSON.stringify(
        Object.keys(queries)
          .sort()
          .map((id) => [id, queries[id]]),
      ),
    ] as const,
};

// react-query dedupes by key, so every chart subscribing fires one request.
type SharedMetricsQuery = {
  queryKey: ReturnType<typeof dashboardMetricsKeys.batch>;
  queryFn: () => Promise<BatchedResults>;
  enabled: boolean;
};

type PinMetrics = {
  rows: MetricsQueryRow[] | undefined;
  error: Error | null;
  isLoading: boolean;
};

const fetchBatchedMetrics = async (
  baseUrl: string,
  queries: Record<string, PinBatchedQuery>,
  dataSource: string | undefined,
): Promise<BatchedResults> => {
  const endpoint = `https://${baseUrl}${METRICS_QUERY_PATH}`;
  const body: { queries: Record<string, PinBatchedQuery>; context?: string } = {
    queries,
  };
  if (dataSource !== undefined) {
    body.context = dataSource;
  }
  const { data } = await axios.post<MetricsQueryResponse>(endpoint, body);
  return data.results ?? {};
};

const selectPinResult = (
  results: BatchedResults,
  pinId: string,
): Omit<PinMetrics, "isLoading"> => {
  const result = results[pinId];
  // Missing slice = contract violation; an error beats a misleading empty chart.
  if (!result) {
    return {
      rows: undefined,
      error: new Error("No result returned for this chart"),
    };
  }
  if (result.error !== undefined) {
    return { rows: undefined, error: new Error(result.error) };
  }
  const { columns, rows } = result;
  const mapped = (rows ?? []).map((row) =>
    Object.fromEntries((columns ?? []).map((col, i) => [col, row[i]])),
  );
  return { rows: mapped, error: null };
};

const DashboardMetricsContext = createContext<SharedMetricsQuery | null>(null);

// Per-chart `select` + structural sharing: a chart re-renders only when its
// own slice changes, not when a sibling's data arrives.
export const usePinMetrics = (pinId: string): PinMetrics => {
  const shared = useContext(DashboardMetricsContext);
  if (!shared) {
    throw new Error(
      "usePinMetrics must be used within a DashboardMetricsProvider",
    );
  }
  const { data, error, isLoading } = useQuery({
    ...shared,
    staleTime: Infinity,
    select: (results) => selectPinResult(results, pinId),
  });
  return { rows: data?.rows, error: error ?? data?.error ?? null, isLoading };
};

type DashboardMetricsRefresh = {
  refresh: () => void;
  isRefreshing: boolean;
  lastUpdatedAt: number | undefined;
};

export const useDashboardMetricsRefresh = (): DashboardMetricsRefresh => {
  const shared = useContext(DashboardMetricsContext);
  if (!shared) {
    throw new Error(
      "useDashboardMetricsRefresh must be used within a DashboardMetricsProvider",
    );
  }
  const queryClient = useQueryClient();
  const { dataUpdatedAt, isFetching } = useQuery({
    ...shared,
    staleTime: Infinity,
  });
  return {
    refresh: () =>
      void queryClient.invalidateQueries({ queryKey: shared.queryKey }),
    isRefreshing: isFetching,
    lastUpdatedAt: dataUpdatedAt > 0 ? dataUpdatedAt : undefined,
  };
};

export const DashboardMetricsProvider: React.FC<{
  pins: Pin[];
  timeWindow: StrictDateRange;
  dataSource?: string;
  children: React.ReactNode;
}> = ({ pins, timeWindow, dataSource, children }) => {
  const { data: workspace } = useCurrentWorkspace();
  const baseUrl = workspace?.base_url;
  const [environment] = useEnvironment();

  const queries = buildPinBatchedQueries(pins, environment, timeWindow);

  const shared: SharedMetricsQuery = {
    queryKey: dashboardMetricsKeys.batch(baseUrl, dataSource, queries),
    queryFn: () => {
      if (!baseUrl) throw new Error("Workspace not loaded");
      return fetchBatchedMetrics(baseUrl, queries, dataSource);
    },
    enabled: Boolean(baseUrl) && Object.keys(queries).length > 0,
  };

  return (
    <DashboardMetricsContext value={shared}>{children}</DashboardMetricsContext>
  );
};
